/**
 * BlogPage - Public blog with articles
 * Blog posts are created only by admin through Admin Panel
 */

import { sanitizeHtml, contentText } from '../utils/sanitizeHtml';
import { Helmet } from 'react-helmet';
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { 
  collection, 
  query, 
  orderBy, 
  onSnapshot,
  where,
  doc,
  getDoc,
  getDocs,
  runTransaction,
  serverTimestamp,
  limit
} from 'firebase/firestore';
import { db } from '../utils/firebase-config';
import { useParams, Link } from 'react-router-dom';
import './labcss/blog.css';
import Breadcrumbs from '../Components/Breadcrumbs';
import Footer from '../Components/Footer';
import SignInPrompt from '../Components/SignInPrompt';
import { ref, getDownloadURL } from 'firebase/storage';
import { storage } from '../utils/firebase-config';
import { useAuth } from '../contexts/AuthContext';

const getFeaturedImage = (post) =>
  post?.featuredImage ||
  post?.featuredImageUrl ||
  post?.imageUrl ||
  post?.coverImage ||
  post?.cover ||
  '';

const normalizeStoragePath = (src) => {
  if (!src) return '';
  if (src.startsWith('/')) return src.slice(1);
  return src;
};

const shouldResolveStorageSrc = (src) => {
  if (!src) return false;
  if (src.startsWith('gs://')) return true;
  if (src.includes('firebasestorage.googleapis.com')) return true;
  const normalized = normalizeStoragePath(src);
  return normalized.startsWith('blog/') || normalized.startsWith('blog/images/');
};

const resolveImageSrc = async (src) => {
  if (!src || !storage) return src;
  if (!shouldResolveStorageSrc(src)) return src;
  try {
    const storageRef =
      src.startsWith('gs://') || src.includes('firebasestorage.googleapis.com')
        ? ref(storage, src)
        : ref(storage, normalizeStoragePath(src));
    return await getDownloadURL(storageRef);
  } catch (err) {
    console.warn('Failed to resolve image URL:', src, err);
    return src;
  }
};

const resolveContentImages = async (html) => {
  if (!html || typeof DOMParser === 'undefined') return html;
  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    const imgs = Array.from(doc.querySelectorAll('img'));
    if (imgs.length === 0) return html;

    const srcs = await Promise.all(
      imgs.map(async (img) => {
        const src = img.getAttribute('src') || '';
        const resolved = await resolveImageSrc(src);
        return { img, resolved };
      })
    );

    srcs.forEach(({ img, resolved }) => {
      if (resolved) img.setAttribute('src', resolved);
    });

    return doc.body.innerHTML;
  } catch (err) {
    console.warn('Failed to resolve content images:', err);
    return html;
  }
};

const stripHtml = (html) => {
  if (!html) return '';
  if (typeof DOMParser === 'undefined') return html;
  const doc = new DOMParser().parseFromString(html, 'text/html');
  return doc.body.textContent || '';
};

const buildSearchText = (post) => {
  const tagText = Array.isArray(post.tags) ? post.tags.join(' ') : '';
  return [
    post.title,
    post.excerpt,
    post.category,
    post.authorName,
    tagText,
    stripHtml(post.content)
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
};

const getLikeCount = (post) => Number(post.likeCount) || 0;

// Posts without a photo (including library articles) get a plain cover with their category.
const CategoryCover = ({ category }) => (
  <div className={`blog-cover cover-${String(category || 'General').toLowerCase().replace(/[^a-z]+/g, '-')}`} aria-hidden="true">
    <span className="blog-cover-label">{category || 'General'}</span>
  </div>
);

const getReadingTime = (content) => {
  if (!content) return 1;
  const text = stripHtml(content);
  const words = text.trim().split(/\s+/).length;
  return Math.max(1, Math.round(words / 200));
};

// Blog Post Card
const BlogPostCard = ({ post, isLiked, onLike }) => {
  const featuredImage = post._resolvedFeaturedImage || getFeaturedImage(post);
  const publishDate = post.publishedAt?.toDate?.()?.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  }) || 'Recently';
  const likeCount = getLikeCount(post);
  const readTime = getReadingTime(post.content);

  return (
    <article className="blog-card">
      <div className="blog-card-image">
        {featuredImage ? <img src={featuredImage} alt={post.title} loading="lazy" /> : <CategoryCover category={post.category} />}
      </div>
      <div className="blog-card-content">
        <div className="blog-card-meta">
          <span className="blog-category">{post.category || 'General'}</span>
          <span className="blog-date">{publishDate}</span>
          <span className="blog-read-time">{readTime} min read</span>
        </div>
        <h2 className="blog-card-title">
          <Link to={`/blog/${post.slug || post.id}`}>{post.title}</Link>
        </h2>
        <p className="blog-card-excerpt">
          {post.excerpt || contentText(post.content).substring(0, 150) + '...'}
        </p>
        <div className="blog-card-footer">
          <span className="blog-author">By {post.authorName || 'Soap Lab Team'}</span>
          <button
            type="button"
            className={`blog-like-btn ${isLiked ? 'liked' : ''}`}
            onClick={(e) => {
              e.preventDefault();
              onLike(post);
            }}
            aria-pressed={isLiked}
          >
            ♥ {likeCount}
          </button>
          <Link to={`/blog/${post.slug || post.id}`} className="read-more">
            Read More →
          </Link>
        </div>
      </div>
    </article>
  );
};

// Single Blog Post View
const BlogPostView = ({ postId, onPostLoaded, isLiked: isLikedProp, onLike }) => {
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [resolvedFeaturedImage, setResolvedFeaturedImage] = useState('');
  const [resolvedContent, setResolvedContent] = useState('');
  const [localIsLiked, setLocalIsLiked] = useState(false);
  const [copied, setCopied] = useState(false);
  const { user, isAdmin } = useAuth();
  const canPreview = isAdmin();
  const loadedPostId = post?.id;

  // Check like status for this specific post
  useEffect(() => {
    let active = true;
    const checkLikeStatus = async () => {
      if (!user || !loadedPostId) {
        setLocalIsLiked(false);
        return;
      }
      try {
        const likeRef = doc(db, 'blogPosts', loadedPostId, 'likes', user.uid);
        const likeSnap = await getDoc(likeRef);
        if (active) setLocalIsLiked(likeSnap.exists());
      } catch (err) {
        console.warn('Failed to check like status:', err);
      }
    };
    checkLikeStatus();
    return () => { active = false; };
  }, [user, loadedPostId]);

  // Use local state if isLikedProp is always false (single post view)
  const isLiked = isLikedProp || localIsLiked;

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setPost(null);
    setResolvedContent('');
    setResolvedFeaturedImage('');
    const fetchPost = async () => {
      let foundPost = null;

      // First try to get by document ID (faster)
      try {
        const docRef = doc(db, 'blogPosts', postId);
        const docSnap = await getDoc(docRef);
        
        if (docSnap.exists()) {
          const data = docSnap.data();
          // Only administrators can read unpublished posts; they see them as a preview.
          if (data.status === 'published' || canPreview) {
            foundPost = { id: docSnap.id, ...data, _preview: data.status !== 'published' };
          }
        }
      } catch (err) {
        // getDoc fails with permission-denied if doc doesn't exist
        // (Firestore rules can't evaluate resource.data on missing docs)
        console.log('Doc ID lookup failed, trying slug...', err.code);
      }
      
      // Fallback: search by slug (with status filter for Firestore rules)
      if (!foundPost) {
        try {
          const q = query(
            collection(db, 'blogPosts'),
            where('slug', '==', postId),
            where('status', '==', 'published')
          );
          const snapshot = await getDocs(q);
          
          if (!snapshot.empty) {
            foundPost = { id: snapshot.docs[0].id, ...snapshot.docs[0].data() };
          }
        } catch (err) {
          console.error('Slug lookup failed:', err);
        }
      }

      if (cancelled) return;
      if (foundPost) {
        setPost(foundPost);
        if (onPostLoaded) {
          onPostLoaded(foundPost);
        }
      }
      setLoading(false);
    };
    
    fetchPost();
    return () => { cancelled = true; };
  }, [postId, onPostLoaded, canPreview]);

  useEffect(() => {
    let active = true;
    const resolvePostAssets = async () => {
      if (!post) return;
      const featured = await resolveImageSrc(getFeaturedImage(post));
      if (active) setResolvedFeaturedImage(featured || '');
      const content = await resolveContentImages(post.content || '');
      if (active) setResolvedContent(content);
    };
    resolvePostAssets();
    return () => { active = false; };
  }, [post]);

  if (loading) {
    return <div className="loading-state">Loading post...</div>;
  }

  if (!post) {
    return (
      <div className="not-found">
        <h2>Post Not Found</h2>
        <p>The blog post you're looking for doesn't exist.</p>
        <Link to="/blog">Back to Blog</Link>
      </div>
    );
  }

  const publishDate = (post.publishedAt?.toDate?.() || (post._preview && post.scheduledFor?.toDate?.()))?.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  }) || 'Recently';
  const featuredImage = resolvedFeaturedImage || getFeaturedImage(post);
  const photoCredit = post.featuredImageCredit;
  const likeCount = getLikeCount(post);
  const readTime = getReadingTime(post.content);

  const handleShare = async () => {
    const url = window.location.href;
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // fallback
    }
  };

  const handleLikeClick = async () => {
    if (!post) return;
    const result = await onLike(post);
    if (result !== 0) {
      setLocalIsLiked(result > 0);
      setPost((prev) =>
        prev ? { ...prev, likeCount: Math.max(getLikeCount(prev) + result, 0) } : prev
      );
    }
  };

  return (
    <article className="blog-post-single">
      <Helmet>
        <title>{post.title} | SoapLab</title>
        <meta name="description" content={post.excerpt || contentText(post.content).slice(0, 160)} />
        <meta property="og:title" content={post.title} />
        <meta property="og:type" content="article" />
        <meta property="og:description" content={post.excerpt || contentText(post.content).slice(0, 160)} />
        <link rel="canonical" href={`https://soaplab.in/blog/${post.slug || post.id}`} />
        {post._preview && <meta name="robots" content="noindex" />}
      </Helmet>
      {post._preview && (
        <div className="post-preview-banner" role="status">
          <strong>Preview.</strong> This post is {post.status === 'scheduled' ? 'scheduled' : 'a draft'}
          {post.scheduledFor?.toDate && ` for ${post.scheduledFor.toDate().toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' })}`}.
          {' '}Only administrators can see it. <Link to="/admin?tab=blog">Manage in the blog calendar</Link>
        </div>
      )}
      <div className="post-header">
        <div className="post-meta">
          <span className="post-category">{post.category || 'General'}</span>
          <span className="post-date">{publishDate}</span>
          <span className="blog-read-time">{readTime} min read</span>
          <span className="post-author">By {post.authorName || 'SoapLab Team'}</span>
        </div>
        {!post._preview && (
          <div className="post-actions-bar">
            <button
              type="button"
              className={`blog-like-btn post-like-btn ${isLiked ? 'liked' : ''}`}
              onClick={handleLikeClick}
              aria-pressed={isLiked}
            >
              {isLiked ? '♥' : '♡'} {likeCount} {likeCount === 1 ? 'Like' : 'Likes'}
            </button>
            <button type="button" className="blog-share-btn" onClick={handleShare}>
              {copied ? 'Copied!' : 'Copy Link'}
            </button>
          </div>
        )}
      </div>

      {featuredImage && (
        <figure className="post-featured-image">
          <img src={featuredImage} alt={post.title} />
          {photoCredit?.source && (
            <figcaption className="post-photo-credit">
              Photo: {photoCredit.name ? `${photoCredit.name} / ` : ''}
              {/^https:\/\//.test(photoCredit.url || '')
                ? <a href={photoCredit.url} target="_blank" rel="noopener noreferrer">{photoCredit.source}</a>
                : photoCredit.source}
            </figcaption>
          )}
        </figure>
      )}

      <div
        className="post-content"
        dangerouslySetInnerHTML={{ __html: sanitizeHtml(resolvedContent || post.content) }}
      />

      {post.tags && post.tags.length > 0 && (
        <p className="post-tags">Topics: {post.tags.join(' · ')}</p>
      )}

      <aside className="post-tools-cta" aria-label="SoapLab tools">
        <h3>Plan your next batch in SoapLab</h3>
        <p>Check lye and water for any recipe, keep your batches and stock in one place, and see what each bar really costs.</p>
        <div className="post-tools-links">
          <Link to="/calculator">Open the lye calculator</Link>
          <Link to="/templates">Browse recipe templates</Link>
          <Link to={user ? '/batches' : '/signup'}>{user ? 'Record a batch' : 'Track batches and stock free'}</Link>
        </div>
      </aside>

      <p className="post-disclaimer">
        This article is general information, not professional, medical or legal advice. Soapmaking with lye involves caustic chemicals:
        follow safety precautions, check every recipe in the lye calculator, test small batches, and follow the rules where you make and sell soap.
        See our <Link to="/terms">Terms of Use</Link>.
      </p>

      {/* Like bar at bottom */}
      {!post._preview && (
        <div className="post-bottom-bar">
          <button
            type="button"
            className={`blog-like-btn post-like-btn ${isLiked ? 'liked' : ''}`}
            onClick={handleLikeClick}
            aria-pressed={isLiked}
          >
            {isLiked ? '♥' : '♡'} {likeCount} {likeCount === 1 ? 'Like' : 'Likes'}
          </button>
          <button type="button" className="blog-share-btn" onClick={handleShare}>
            {copied ? 'Copied!' : 'Copy Link'}
          </button>
        </div>
      )}
    </article>
  );
};

// Related Posts Component
const RelatedPosts = ({ currentPostId, posts, likedPostIds, onLike, currentCategory }) => {
  const related = useMemo(() => {
    return posts
      .filter(p => p.id !== currentPostId && p.category === currentCategory)
      .slice(0, 3);
  }, [posts, currentPostId, currentCategory]);

  if (related.length === 0) return null;

  return (
    <div className="related-posts">
      <h3 className="related-posts-heading">More in {currentCategory || 'this category'}</h3>
      <div className="related-posts-grid">
        {related.map(post => (
          <BlogPostCard
            key={post.id}
            post={post}
            isLiked={likedPostIds.has(post.id)}
            onLike={onLike}
          />
        ))}
      </div>
    </div>
  );
};

// Main Blog Page
export default function BlogPage() {
  const { postId } = useParams();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [categories, setCategories] = useState([]);
  const [selectedTags, setSelectedTags] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('newest');
  const [currentPostTitle, setCurrentPostTitle] = useState('');
  const [currentPostId, setCurrentPostId] = useState('');
  const [likedPostIds, setLikedPostIds] = useState(new Set());
  const [signInPrompt, setSignInPrompt] = useState('');
  const [showLikedOnly, setShowLikedOnly] = useState(false);
  const [postLimit, setPostLimit] = useState(24);
  const [hasMore, setHasMore] = useState(false);
  const [loadError, setLoadError] = useState('');
  const { user } = useAuth();
  const handlePostLoaded = useCallback((post) => {
    setCurrentPostTitle(post.title || '');
    setCurrentPostId(post.id || '');
  }, []);

  useEffect(() => {
    setCurrentPostTitle('');
    setCurrentPostId('');
  }, [postId]);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setLoadError('');
    const pageSize = postId ? 6 : postLimit;
    const q = query(
      collection(db, 'blogPosts'),
      where('status', '==', 'published'),
      orderBy('publishedAt', 'desc'),
      limit(pageSize + 1)
    );

    const unsubscribe = onSnapshot(q, async (snapshot) => {
      const postsData = snapshot.docs.slice(0, pageSize).map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      const resolvedPosts = await Promise.all(
        postsData.map(async (post) => {
          const featured = await resolveImageSrc(getFeaturedImage(post));
          return { ...post, _resolvedFeaturedImage: featured };
        })
      );
      if (!active) return;
      setHasMore(snapshot.docs.length > pageSize);
      setPosts(resolvedPosts);
      
      // Extract unique categories
      const cats = [...new Set(postsData.map(p => p.category).filter(Boolean))];
      setCategories(cats);
      
      setLoading(false);
    }, (error) => {
      console.error('Error fetching blog posts:', error);
      if (!active) return;
      setLoadError('Posts could not be loaded. Please try again shortly.');
      setLoading(false);
    });

    return () => { active = false; unsubscribe(); };
  }, [postId, postLimit]);

  // Load liked post IDs once when user or post IDs change (not on every posts update)
  const postIds = useMemo(() => posts.map((p) => p.id).sort().join(','), [posts]);
  useEffect(() => {
    const loadLikedPosts = async () => {
      if (!user || !postIds) {
        setLikedPostIds(new Set());
        return;
      }
      const ids = postIds.split(',');
      const liked = new Set();
      await Promise.all(
        ids.map(async (id) => {
          try {
            const likeRef = doc(db, 'blogPosts', id, 'likes', user.uid);
            const likeSnap = await getDoc(likeRef);
            if (likeSnap.exists()) liked.add(id);
          } catch (err) {
            console.warn('Failed to check like status:', err);
          }
        })
      );
      setLikedPostIds(liked);
    };
    loadLikedPosts();
  }, [user, postIds]);

  const handleLike = async (post) => {
    if (!user) {
      setSignInPrompt('like posts');
      return 0;
    }

    const postRef = doc(db, 'blogPosts', post.id);
    const likeRef = doc(db, 'blogPosts', post.id, 'likes', user.uid);
    try {
      const result = await runTransaction(db, async (transaction) => {
        const [postSnap, likeSnap] = await Promise.all([transaction.get(postRef), transaction.get(likeRef)]);
        if (!postSnap.exists()) throw new Error('This article is no longer available.');
        const liked = !likeSnap.exists();
        const currentCount = Number(postSnap.data().likeCount) || 0;
        const count = Math.max(0, currentCount + (liked ? 1 : -1));
        if (liked) transaction.set(likeRef, { userId: user.uid, createdAt: serverTimestamp() });
        else transaction.delete(likeRef);
        transaction.update(postRef, { likeCount: count });
        return { liked, count };
      });
      setLikedPostIds((previous) => {
        const next = new Set(previous);
        if (result.liked) next.add(post.id); else next.delete(post.id);
        return next;
      });
      setPosts((previous) => previous.map((entry) => entry.id === post.id ? { ...entry, likeCount: result.count } : entry));
      return result.liked ? 1 : -1;
    } catch (error) {
      alert(error.message || 'Your like could not be saved.');
      return 0;
    }
  };

  const allTags = useMemo(() => {
    const tagSet = new Set();
    posts.forEach((post) => {
      if (Array.isArray(post.tags)) {
        post.tags.forEach((tag) => tag && tagSet.add(tag));
      }
    });
    return Array.from(tagSet).sort();
  }, [posts]);

  // Filter and search posts (client-side)
  const filteredPosts = useMemo(() => {
    const queryText = searchQuery.trim().toLowerCase();
    let result = [...posts];

    if (showLikedOnly && user) {
      result = result.filter((p) => likedPostIds.has(p.id));
    }

    if (selectedCategory !== 'all') {
      result = result.filter((p) => p.category === selectedCategory);
    }

    if (selectedTags.length > 0) {
      result = result.filter(
        (p) => Array.isArray(p.tags) && selectedTags.every((t) => p.tags.includes(t))
      );
    }

    if (queryText) {
      result = result.filter((p) => buildSearchText(p).includes(queryText));
    }

    result.sort((a, b) => {
      if (sortBy === 'oldest') {
        return (a.publishedAt?.toMillis?.() || 0) - (b.publishedAt?.toMillis?.() || 0);
      }
      if (sortBy === 'popular') {
        return getLikeCount(b) - getLikeCount(a);
      }
      return (b.publishedAt?.toMillis?.() || 0) - (a.publishedAt?.toMillis?.() || 0);
    });

    return result;
  }, [posts, selectedCategory, selectedTags, searchQuery, sortBy, showLikedOnly, likedPostIds, user]);

  // If viewing single post
  if (postId) {
    return (
      <div className="sl-page blog-page single-post-page">
        <div className="pageheader borderleft">
          <Breadcrumbs
            items={[
              { label: 'Home', to: '/' },
              { label: 'Blog', to: '/blog' },
              { label: currentPostTitle || 'Post' }
            ]}
          />
          <h1 className="pageheading post-pageheading">{currentPostTitle || 'Blog'}</h1>
        </div>
        <div className="blog-page-inner">
          <BlogPostView
            postId={postId}
            onPostLoaded={handlePostLoaded}
            isLiked={currentPostId ? likedPostIds.has(currentPostId) : false}
            onLike={handleLike}
          />
          {/* Related Posts */}
          {currentPostId && posts.length > 0 && (
            <RelatedPosts
              currentPostId={currentPostId}
              posts={posts}
              likedPostIds={likedPostIds}
              onLike={handleLike}
              currentCategory={posts.find(p => p.id === currentPostId)?.category}
            />
          )}
        </div>
        <SignInPrompt intent={signInPrompt} onClose={() => setSignInPrompt('')} />
        <Footer />
      </div>
    );
  }

  return (
    <div className="sl-page blog-page">
      <Helmet>
        <title>Soapmaking Recipes & Business Guides | SoapLab</title>
        <meta name="description" content="Soapmaking recipes, production tips and practical guides for running your soap business." />
        <link rel="canonical" href="https://soaplab.in/blog" />
      </Helmet>
      <div className="pageheader borderleft">
        <Breadcrumbs
          items={[
            { label: 'Home', to: '/' },
            { label: 'Blog' }
          ]}
        />
        <div className="pageheading">Blog</div>
      </div>

      <div className="blog-page-inner borderleft">
        <div className="blog-filters">
          <div className="blog-search">
            <input
              type="text"
              placeholder="Search posts…"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          <div className="blog-filter-row">
            <select
              className="blog-filter-select"
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
            >
              <option value="all">All Categories</option>
              {categories.map((cat) => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </select>

            <select
              className="blog-filter-select"
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
            >
              <option value="newest">Newest</option>
              <option value="oldest">Oldest</option>
              <option value="popular">Most Popular</option>
            </select>

            {user && (
              <button
                type="button"
                className={`blog-liked-toggle ${showLikedOnly ? 'active' : ''}`}
                onClick={() => setShowLikedOnly(!showLikedOnly)}
              >
                {showLikedOnly ? 'Liked Posts' : 'Liked Posts'}
              </button>
            )}
          </div>

          <div className="blog-results-count">
            {filteredPosts.length} {filteredPosts.length === 1 ? 'post' : 'posts'}
            {showLikedOnly ? ' you liked' : ''}
          </div>

          {allTags.length > 0 && (
            <details className="blog-topics" open={selectedTags.length > 0 || undefined}>
            <summary>Browse by topic{selectedTags.length ? ` (${selectedTags.length} selected)` : ''}</summary>
            <div className="blog-tags">
              {allTags.map((tag) => {
                const active = selectedTags.includes(tag);
                return (
                  <button
                    key={tag}
                    type="button"
                    className={`tag-chip ${active ? 'active' : ''}`}
                    onClick={() =>
                      setSelectedTags((prev) =>
                        active ? prev.filter((t) => t !== tag) : [...prev, tag]
                      )
                    }
                  >
                    #{tag}
                  </button>
                );
              })}
            </div>
            </details>
          )}
        </div>

        {/* Posts Grid */}
        {loadError ? <p role="alert" className="loading-state">{loadError}</p> : loading ? (
          <div className="loading-state">Loading posts...</div>
        ) : filteredPosts.length === 0 ? (
          <div className="empty-state">
            <h3>{showLikedOnly ? 'No liked posts yet' : 'No blog posts yet'}</h3>
            <p>{showLikedOnly ? 'Like some posts and they\'ll appear here!' : 'Check back soon for articles and tutorials!'}</p>
            {showLikedOnly && (
              <button
                type="button"
                className="blog-liked-toggle active"
                style={{ marginTop: 12 }}
                onClick={() => setShowLikedOnly(false)}
              >
                Show All Posts
              </button>
            )}
          </div>
        ) : (
          <div className="blog-grid">
            {/* Featured Post */}
            {filteredPosts.length > 0 && (
              <div className="featured-post">
                <BlogPostCard
                  post={filteredPosts[0]}
                  isLiked={likedPostIds.has(filteredPosts[0].id)}
                  onLike={handleLike}
                />
              </div>
            )}
            
            {/* Other Posts */}
            <div className="posts-grid">
              {filteredPosts.slice(1).map(post => (
                <BlogPostCard
                  key={post.id}
                  post={post}
                  isLiked={likedPostIds.has(post.id)}
                  onLike={handleLike}
                />
              ))}
            </div>
          </div>
        )}
        {hasMore && !loading && !loadError && (
          <button type="button" className="blog-liked-toggle" style={{ margin: '24px auto', display: 'block' }} onClick={() => setPostLimit((count) => count + 24)}>
            Load more posts
          </button>
        )}
      </div>
      <SignInPrompt intent={signInPrompt} onClose={() => setSignInPrompt('')} />
        <Footer />
    </div>
  );
}
